import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, X } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Marquee } from "../components/Marquee";
import { IMAGES, isVideo } from "../lib/site";
import { useContent } from "../lib/content";

const Lightbox = ({ photo, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[60] bg-[#3c003a]/95 backdrop-blur-md grid place-items-center p-6"
    onClick={onClose}
    data-testid="gallery-lightbox"
  >
    <button
      onClick={onClose}
      aria-label="Close lightbox"
      data-testid="lightbox-close-btn"
      className="absolute top-6 right-6 w-11 h-11 rounded-full border border-white/25 text-white grid place-items-center hover:border-[#c9006f] hover:text-[#ffc4e4] transition-colors"
    >
      <X size={20} />
    </button>
    <motion.figure
      initial={{ scale: 0.92, y: 20 }}
      animate={{ scale: 1, y: 0 }}
      exit={{ scale: 0.94, y: 10 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="max-w-4xl w-full"
      onClick={(e) => e.stopPropagation()}
    >
      {isVideo(photo.src) ? (
        <video
          src={photo.src}
          controls
          autoPlay
          loop
          playsInline
          className="w-full max-h-[78vh] object-contain rounded-2xl"
        />
      ) : (
        <img
          src={photo.src}
          alt={photo.label}
          className="w-full max-h-[78vh] object-contain rounded-2xl"
        />
      )}
      <figcaption className="mt-4 flex items-baseline justify-between">
        <span className="font-serif italic text-white text-xl">{photo.label}</span>
        <span className="text-[#c9006f] text-xs uppercase tracking-[0.22em] font-semibold">
          {photo.cat}
        </span>
      </figcaption>
    </motion.figure>
  </motion.div>
);

export default function GalleryPage() {
  const { gallery, galleryCategories } = useContent();
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  const usedCats = new Set(gallery.map((p) => p.cat));
  const ordered = galleryCategories.map((c) => c.name).filter((n) => usedCats.has(n));
  const strays = Array.from(usedCats).filter((n) => !ordered.includes(n));
  const filters = ["All", ...ordered, ...strays];
  const shown = filter === "All" ? gallery : gallery.filter((p) => p.cat === filter);

  return (
    <div data-testid="gallery-page">
      <Navbar />

      <header className="bg-[#3c003a] relative grain pt-40 pb-20 overflow-hidden">
        <img
          src={IMAGES.leaves}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#3c003a]/60 to-[#3c003a]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            data-testid="gallery-back-link"
            className="inline-flex items-center gap-2 text-white/60 hover:text-[#ffc4e4] text-sm mb-8 transition-colors"
          >
            <ArrowLeft size={16} /> Back to home
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05] mb-5"
          >
            The Full <em className="text-[#c9006f]">Gallery</em>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.9 }}
            className="text-white/60 text-base md:text-lg max-w-xl leading-[1.7]"
          >
            Real moments from island weddings, galas, and parties — browse by
            event type and picture your own celebration.
          </motion.p>
        </div>
      </header>

      <main className="bg-[#fafaf7] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-3 mb-12" data-testid="gallery-filters">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`gallery-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 ${
                  filter === f
                    ? "bg-[#91008d] text-white shadow-[0_8px_24px_rgba(145,0,141,0.25)]"
                    : "bg-white border border-black/8 text-[#121212]/60 hover:border-[#c9006f] hover:text-[#91008d]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <motion.div layout className="columns-1 sm:columns-2 lg:columns-3 gap-5">
            <AnimatePresence mode="popLayout">
              {shown.map((photo, i) => (
                <motion.div
                  key={photo.src + photo.label}
                  layout
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.94 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="break-inside-avoid mb-5"
                >
                  <button
                    onClick={() => setActive(photo)}
                    data-testid={`gallery-photo-${photo.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group relative block w-full overflow-hidden rounded-3xl glow-card shadow-[0_8px_30px_rgba(145,0,141,0.08)] text-left"
                    style={{ "--glow-delay": `${(i % 6) * 0.7}s` }}
                  >
                    {isVideo(photo.src) ? (
                      <video
                        src={photo.src}
                        muted
                        loop
                        autoPlay
                        playsInline
                        className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <img
                        src={photo.src}
                        alt={photo.label}
                        loading="lazy"
                        className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    )}
                    {(photo.badge || photo.is360) && (
                      <span className="absolute top-4 right-4 rounded-full bg-[#c9006f] text-white text-[10px] font-bold tracking-[0.18em] uppercase px-3 py-1.5">
                        {{ "360": "360°", video: "Video", boomerang: "Boomerang" }[photo.badge] || "360°"}
                      </span>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#3c003a]/75 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute bottom-4 left-5 right-5 flex items-baseline justify-between opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                      <span className="font-serif italic text-white text-lg">{photo.label}</span>
                      <span className="text-[#ffc4e4] text-[10px] uppercase tracking-[0.2em] font-semibold shrink-0 ml-3">
                        {photo.cat}
                      </span>
                    </div>
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </main>

      <Marquee dark />

      <section className="bg-[#3c003a] relative grain py-20 text-center">
        <div className="relative z-10 max-w-2xl mx-auto px-6">
          <h2 className="font-serif text-white text-3xl sm:text-4xl mb-6">
            Want photos like these at <em className="text-[#c9006f]">your event?</em>
          </h2>
          <Link
            to="/"
            state={{ scrollTo: "#contact" }}
            data-testid="gallery-cta-book-btn"
            className="btn-gold inline-block rounded-full px-9 py-4 text-base font-bold tracking-wide"
          >
            Book Your Date
          </Link>
        </div>
      </section>

      <Footer />

      <AnimatePresence>
        {active && <Lightbox photo={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </div>
  );
}
